// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module DeviceBreakdown
 * @package @geenius/analytics/solidjs-css
 * @description Renders the SolidJS CSS device-breakdown visualization that
 * compares desktop, tablet, and mobile traffic. The component pairs a
 * proportional bar with a simple legend for quick distribution scans.
 */

import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@geenius/ui/solidjs-css";
import { type Component, For } from "solid-js";
import { createAnalyticsCopy } from "../lib/analyticsCopy";
import { type AnalyticsErrorStateValue, ErrorState } from "./ErrorState";

/**
 * Props for rendering the SolidJS CSS device-breakdown widget.
 *
 * @property breakdown Device counts grouped by desktop, tablet, and mobile.
 * @property class Optional class name merged into the wrapper.
 */
export interface DeviceBreakdownProps {
  breakdown: { desktop: number; tablet: number; mobile: number };
  error?: AnalyticsErrorStateValue;
  onRetry?: () => void;
  class?: string;
}

/**
 * Renders the SolidJS CSS device-breakdown widget.
 *
 * @param props Device counts and optional wrapper class.
 * @returns A device distribution widget for analytics dashboards.
 */
export const DeviceBreakdown: Component<DeviceBreakdownProps> = (props) => {
  const total = (): number =>
    props.breakdown.desktop + props.breakdown.tablet + props.breakdown.mobile ||
    1;
  const pct = (value: number): string =>
    `${((value / total()) * 100).toFixed(1)}%`;
  const copy = createAnalyticsCopy();

  const segments = () => [
    {
      device: "desktop",
      value: props.breakdown.desktop,
      icon: "💻",
      variant: "secondary" as const,
    },
    {
      device: "tablet",
      value: props.breakdown.tablet,
      icon: "📟",
      variant: "warning" as const,
    },
    {
      device: "mobile",
      value: props.breakdown.mobile,
      icon: "📱",
      variant: "success" as const,
    },
  ];

  if (props.error) {
    return (
      <Card class={props.class}>
        <CardHeader>
          <CardTitle data-analytics-section-title>
            {copy.t("analytics.devices.title")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ErrorState error={props.error} onRetry={props.onRetry} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card class={props.class}>
      <CardHeader>
        <CardTitle data-analytics-section-title>
          {copy.t("analytics.devices.title")}
        </CardTitle>
      </CardHeader>
      <CardContent
        class="gn-analytics-device-breakdown"
        data-analytics-device-breakdown
      >
        <For each={segments()}>
          {(segment) => (
            <div data-analytics-device-item data-device={segment.device}>
              <div class="gn-analytics-device-breakdown__item-header">
                <Badge
                  class={`gn-analytics-device-badge gn-analytics-device-badge--${segment.device}`}
                  variant={segment.variant}
                >
                  {segment.icon} {copy.deviceLabel(segment.device)}
                </Badge>
                <span data-analytics-device-pct>{pct(segment.value)}</span>
              </div>
              <div
                class="gui-progress"
                role="progressbar"
                aria-label={`${copy.deviceLabel(segment.device)} traffic share: ${pct(segment.value)}`}
                aria-valuemin={0}
                aria-valuenow={segment.value}
                aria-valuemax={total()}
              >
                <div
                  class="gui-progress__bar"
                  style={{
                    width: `${Math.min(100, (segment.value / total()) * 100)}%`,
                  }}
                />
              </div>
            </div>
          )}
        </For>
      </CardContent>
    </Card>
  );
};
